import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, IconButton, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from "@mui/material";
import {
  FaChevronUp, FaDesktop, FaDownload, FaFolder, FaRedo, FaTimes, FaWindowMinimize,
} from "react-icons/fa";
import api from "../Api";

// A file-manager window for the folders the server-side console browsers download
// into — the same thing "Show in folder" gives you on the Mac, except the files
// live on the Oracle box so the window has to be ours.
//
// Deliberately NOT a <Dialog>: it floats ABOVE the console, is draggable and
// resizable, and does not block the noVNC screen behind it, so you can watch a
// run and dig through what it produced at the same time.
//
// Two views, like Chrome: "Folders" walks the tree one level at a time, "Recent"
// is the flat newest-first list.

const WIN_Z = 1400;          // menus must clear this, see MenuProps below

const ICONS = {
  ".pdf": "📕", ".csv": "📄", ".xlsx": "📗", ".xls": "📗",
  ".png": "🖼️", ".jpg": "🖼️", ".jpeg": "🖼️",
  ".html": "🌐", ".htm": "🌐", ".zip": "🗜️", ".json": "🧾", ".log": "📜", ".txt": "📃",
};

const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB`
  : n >= 1024 ? `${Math.round(n / 1024)} KB` : `${n} B`);

const fmtAgo = (ms) => {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  return new Date(ms).toLocaleString();
};

export default function ConsoleDownloadsDialog({ open, onClose, source = "flipkart", title }) {
  const [src, setSrc] = useState(source);
  const [sources, setSources] = useState([]);
  const [view, setView] = useState("folders");     // "folders" | "recent"
  const [cwd, setCwd] = useState("");              // path relative to the source root
  const [node, setNode] = useState(null);          // { crumbs, parent, dirs, files }
  const [recent, setRecent] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");   // success text, not an error
  const [min, setMin] = useState(false);

  // Free-floating position, dragged by the title bar.
  const [pos, setPos] = useState({ x: 90, y: 70 });
  const [size, setSize] = useState({ width: 560, height: 460 });
  const drag = useRef(null);
  const resize = useRef(null);

  useEffect(() => { if (open) { setSrc(source); setCwd(""); setMin(false); } }, [open, source]);

  useEffect(() => {
    if (!open) return undefined;
    const move = (e) => {
      if (!drag.current) return;
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - 200, e.clientX - drag.current.dx)),
        y: Math.max(0, Math.min(window.innerHeight - 60, e.clientY - drag.current.dy)),
      });
    };
    const up = () => { drag.current = null; };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const move = (e) => {
      if (!resize.current) return;
      setSize({
        width: Math.max(340, Math.min(window.innerWidth - 16, resize.current.width + e.clientX - resize.current.x)),
        height: Math.max(220, Math.min(window.innerHeight - 16, resize.current.height + e.clientY - resize.current.y)),
      });
      e.preventDefault();
    };
    const up = () => { resize.current = null; };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    api.get("/api/console-downloads/sources")
      .then(({ data }) => setSources((data && data.sources) || []))
      .catch(() => setSources([]));
  }, [open]);

  const load = useCallback(async () => {
    if (!open || !src) return;
    setBusy(true);
    setErr("");
    setNote("");
    try {
      if (view === "folders") {
        const { data } = await api.get("/api/console-downloads/browse",
          { params: { source: src, path: cwd } });
        setNode(data);
      } else {
        const { data } = await api.get("/api/console-downloads/files", { params: { source: src } });
        setRecent((data && data.files) || []);
      }
    } catch (e) {
      if (view === "folders") setNode(null); else setRecent([]);
      setErr(e?.response?.data?.error || e?.message || "Could not read that folder.");
    }
    setBusy(false);
  }, [open, src, cwd, view]);

  useEffect(() => { load(); }, [load]);

  const openFile = useCallback(async (f, asDownload) => {
    setErr("");
    try {
      const { data } = await api.get("/api/console-downloads/file", {
        params: { source: src, p: f.rel, ...(asDownload ? { dl: 1 } : {}) },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      if (asDownload || !f.inline) {
        const a = document.createElement("a");
        a.href = url; a.download = f.name;
        document.body.appendChild(a); a.click(); a.remove();
      } else if (!window.open(url, "_blank", "noopener")) {
        setErr("Popup blocked — allow popups for this site, or use the download arrow.");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || "Could not fetch that file.");
    }
  }, [src]);

  // Put a genuine folder window on the console screen, at whatever we're looking
  // at here. This is what Chrome's own "show in folder" icon would do if the snap
  // could reach a file manager — see the route comment for why it can't.
  const openOnConsole = useCallback(async (rel) => {
    setErr("");
    setNote("");
    // Only a string is a path. Called straight from onClick this arrives as a
    // React event, which axios then tries to serialise -> "circular structure".
    const target = typeof rel === "string" ? rel : cwd;
    try {
      const { data } = await api.post("/api/console-downloads/open-window",
        { source: src, path: target,
          // the manual folder is shared by both consoles -- open it on the
          // screen belonging to the page we were launched from
          display: source === "myntra" ? ":100" : ":99" });
      setNote(`Opened on the console screen (${data.display}): ${data.opened}`);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || "Could not open the folder there.");
    }
  }, [src, cwd, source]);

  const q = search.trim().toLowerCase();
  const dirs = useMemo(
    () => ((node && node.dirs) || []).filter((d) => !q || d.name.toLowerCase().includes(q)),
    [node, q]);
  const files = useMemo(() => {
    const list = view === "folders" ? (node && node.files) || [] : recent;
    return list.filter((f) => !q || f.name.toLowerCase().includes(q));
  }, [view, node, recent, q]);

  if (!open) return null;

  const rowSx = {
    display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.6,
    borderRadius: 1, "&:hover": { bgcolor: "action.hover" },
  };
  const tab = (key, label) => (
    <Chip size="small" label={label} key={key}
          color={view === key ? "primary" : "default"}
          variant={view === key ? "filled" : "outlined"}
          onClick={() => setView(key)} sx={{ height: 22, fontSize: 11 }} />
  );

  return (
    <Paper
      elevation={16}
      sx={{
        position: "fixed", left: pos.x, top: pos.y, zIndex: WIN_Z,
        width: min ? 320 : size.width, maxWidth: "96vw",
        height: min ? "auto" : size.height, maxHeight: "86vh",
        display: "flex", flexDirection: "column", overflow: "hidden",
        border: "1px solid", borderColor: "divider",
      }}
    >
      {/* Title bar — drag handle, like a real window */}
      <Box
        onPointerDown={(e) => {
          if (e.target.closest("button,input,.MuiChip-root,[role=combobox]")) return;
          drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
        }}
        sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.6,
              bgcolor: "primary.main", color: "primary.contrastText", cursor: "move",
              flexShrink: 0 }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, minWidth: 0,
                                          whiteSpace: "nowrap", overflow: "hidden",
                                          textOverflow: "ellipsis" }}>
          {title || "📥 Browser downloads"}
        </Typography>
        <IconButton size="small" onClick={() => setMin((m) => !m)} sx={{ color: "inherit" }}
                    title={min ? "Expand" : "Minimise"}>
          <FaWindowMinimize size={11} />
        </IconButton>
        <IconButton size="small" onClick={onClose} sx={{ color: "inherit" }} title="Close">
          <FaTimes size={13} />
        </IconButton>
      </Box>

      {min ? null : (
        <>
          <Stack direction="row" spacing={0.75} alignItems="center"
                 sx={{ px: 1, py: 0.75, flexWrap: "wrap", gap: 0.75, flexShrink: 0 }}>
            <TextField select size="small" value={src} variant="standard"
                       onChange={(e) => { setSrc(e.target.value); setCwd(""); }}
                       SelectProps={{ MenuProps: { sx: { zIndex: WIN_Z + 10 } } }}
                       sx={{ minWidth: 108 }}>
              {sources.map((s) => (
                <MenuItem key={s.key} value={s.key} disabled={!s.available}>
                  {s.label}{s.available ? "" : " (empty)"}
                </MenuItem>
              ))}
            </TextField>
            {tab("folders", "Folders")}
            {tab("recent", "Recent")}
            <TextField size="small" variant="standard" placeholder="Filter…" value={search}
                       onChange={(e) => setSearch(e.target.value)}
                       sx={{ flex: 1, minWidth: 90 }} />
            <Tooltip title="Open this folder on the console screen (a real file-manager window, above the browser)">
              <IconButton size="small" onClick={() => openOnConsole()}><FaDesktop size={12} /></IconButton>
            </Tooltip>
            <IconButton size="small" onClick={load} title="Refresh"><FaRedo size={11} /></IconButton>
          </Stack>

          {/* Path bar */}
          {view === "folders" ? (
            <Stack direction="row" spacing={0.5} alignItems="center"
                   sx={{ px: 1, pb: 0.5, flexWrap: "wrap", flexShrink: 0 }}>
              <IconButton size="small" disabled={!cwd}
                          onClick={() => setCwd((node && node.parent) || "")} title="Up one folder">
                <FaChevronUp size={11} />
              </IconButton>
              <Typography variant="caption" onClick={() => setCwd("")}
                          sx={{ cursor: "pointer", fontWeight: 700, opacity: 0.85 }}>
                {(node && node.label) || "root"}
              </Typography>
              {((node && node.crumbs) || []).map((c) => (
                <Typography key={c.rel} variant="caption" onClick={() => setCwd(c.rel)}
                            sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
                  {" / "}{c.name}
                </Typography>
              ))}
            </Stack>
          ) : null}

          {err ? (
            <Typography variant="caption" sx={{ px: 1, pb: 0.5, color: "error.main" }}>{err}</Typography>
          ) : note ? (
            <Typography variant="caption" sx={{ px: 1, pb: 0.5, color: "success.main",
                                               wordBreak: "break-all" }}>{note}</Typography>
          ) : null}

          <Box sx={{ flex: 1, overflowY: "auto", px: 0.5, pb: 1 }}>
            {busy ? (
              <Typography variant="body2" sx={{ p: 1 }}>Reading…</Typography>
            ) : dirs.length === 0 && files.length === 0 ? (
              <Typography variant="body2" sx={{ p: 1 }}>This folder is empty.</Typography>
            ) : (
              <>
                {view === "folders" ? dirs.map((d) => (
                  <Box key={d.rel} sx={{ ...rowSx, cursor: "pointer" }}
                       onClick={() => { setCwd(d.rel); setSearch(""); }}>
                    <FaFolder size={15} color="#f5b301" />
                    <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0,
                                                      wordBreak: "break-all" }}>
                      {d.name}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.6, whiteSpace: "nowrap" }}>
                      {d.count} item{d.count === 1 ? "" : "s"}
                    </Typography>
                  </Box>
                )) : null}

                {files.map((f) => (
                  <Box key={f.rel} sx={rowSx}>
                    <Box sx={{ fontSize: 15, lineHeight: 1 }}>{ICONS[f.ext] || "📄"}</Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" onClick={() => openFile(f, false)}
                                  sx={{ fontWeight: 600, wordBreak: "break-all", cursor: "pointer",
                                        color: "primary.main",
                                        "&:hover": { textDecoration: "underline" } }}>
                        {f.name}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {view === "recent" && f.account ? `${f.account} · ` : ""}
                        {view === "recent" && f.folder && f.folder !== "." ? `${f.folder} · ` : ""}
                        {fmtSize(f.size)}{f.ext === ".pdf" && Number.isFinite(f.pages) ? ` · ${f.pages} page${f.pages === 1 ? "" : "s"}` : ""} · {fmtAgo(f.mtime)}
                      </Typography>
                    </Box>
                    {view === "recent" ? (
                      <Tooltip title="Show in folder">
                        <IconButton size="small" onClick={() => {
                          setView("folders");
                          setCwd(f.rel.split("/").slice(0, -1).join("/"));
                          setSearch("");
                        }}>
                          <FaFolder size={12} />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    <Tooltip title="Show on the console screen">
                      <IconButton size="small"
                                  onClick={() => openOnConsole(f.rel.split("/").slice(0, -1).join("/"))}>
                        <FaDesktop size={12} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton size="small" onClick={() => openFile(f, true)}>
                        <FaDownload size={12} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </>
            )}
          </Box>
        </>
      )}
      {min ? null : (
        <Box
          onPointerDown={(e) => {
            resize.current = { x: e.clientX, y: e.clientY, width: size.width, height: size.height };
            try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* best effort */ }
            e.preventDefault();
            e.stopPropagation();
          }}
          title="Hold and drag corner to resize."
          sx={{
            position: "absolute",
            right: 0,
            bottom: 0,
            width: 32,
            height: 32,
            cursor: "nwse-resize",
            zIndex: WIN_Z + 2,
            background: "linear-gradient(135deg, transparent 0 50%, rgba(25,118,210,0.16) 50% 100%)",
            "&::before": {
              content: '""',
              position: "absolute",
              right: 7,
              bottom: 7,
              width: 14,
              height: 14,
              borderRight: "3px solid rgba(25,118,210,0.85)",
              borderBottom: "3px solid rgba(25,118,210,0.85)",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              right: 14,
              bottom: 14,
              width: 7,
              height: 7,
              borderRight: "2px solid rgba(25,118,210,0.65)",
              borderBottom: "2px solid rgba(25,118,210,0.65)",
            },
          }}
        />
      )}
    </Paper>
  );
}
